import { useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { useState } from 'react';
import { applicationInsightsReactPlugin } from '../ApplicationInsightsService';

interface IRenderEventTableInformation {
    rowCount: number;
    columnCount: number;
    visibleColumnCount: number;
    reportId?: number;
    isMultiColumn?: boolean;
}

interface IRenderEventInformation {
    componentName: string;
    durationInMilliseconds: number;
    tableInformation?: IRenderEventTableInformation;
}

// When using this useGetHook, we need to make sure it is at the very start of the component
// This is to ensure that the start time of the render is captured correctly
export const usePerformanceMetricsForTable = (tableName: string) => {
    const [renderStartTimestamp, setRenderStartTimestamp] = useState<number | undefined>(Date.now());
    const [scrollStartTimestamp, setScrollStartTimestamp] = useState<number | undefined>(undefined);

    const currentEvent: IRenderEventInformation = {
        componentName: tableName,
        durationInMilliseconds: 0,
    };

    const trackInitialRender = useTrackEvent<IRenderEventInformation>(
        applicationInsightsReactPlugin,
        'React component initial render',
        currentEvent
    );
    const trackScrollRender = useTrackEvent<IRenderEventInformation>(
        applicationInsightsReactPlugin,
        'React component load after scroll',
        currentEvent
    );

    const resetStartTime = () => {
        setRenderStartTimestamp(Date.now());
    };

    const trackRenderFinished = (tableInformation: IRenderEventTableInformation) => {
        if (renderStartTimestamp) {
            const renderEndTimestamp = Date.now();
            const durationInMilliseconds = renderEndTimestamp - renderStartTimestamp;
            trackInitialRender(() => ({
                ...currentEvent,
                durationInMilliseconds,
                tableInformation,
            }));
            setRenderStartTimestamp(undefined);
        }
    };

    const trackScrollStarted = () => {
        setScrollStartTimestamp(Date.now());
    };

    const trackScrollFinished = (tableInformation: IRenderEventTableInformation) => {
        if (scrollStartTimestamp) {
            const durationInMilliseconds = Date.now() - scrollStartTimestamp;
            trackScrollRender(() => ({
                ...currentEvent,
                durationInMilliseconds,
                tableInformation,
            }));
            setScrollStartTimestamp(undefined);
        }
    };

    return { trackRenderFinished, trackScrollStarted, trackScrollFinished, resetStartTime };
};
